import { useEffect, useRef, useState } from "react";
import { Box, Typography, InputBase } from "@mui/material";
import { Box as BoxIcon, Trash2 } from "lucide-mui";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import {
  tryAcquireWebGLSlot,
  releaseWebGLSlot,
} from "../utils/webglContextPool.js";
import { useTheme } from "../context/ThemeContext";

const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath("/draco/gltf/");

function normalizeFullPath(s) {
  return s ? s.replace(/ /g, "_") : s;
}

export function findNodeByFullPath(object, fullPath) {
  if (!object) return null;
  const fp =
    object.userData?.fullPath ?? object.userData?.gltfExtensions?.fullPath;
  if (fp === fullPath || normalizeFullPath(fp) === normalizeFullPath(fullPath))
    return object;
  for (const child of object.children) {
    const found = findNodeByFullPath(child, fullPath);
    if (found) return found;
  }
  return null;
}

export function cloneNodeRecursive(src) {
  const clone = src.clone();
  clone.children.length = 0;
  for (const child of src.children) {
    clone.add(cloneNodeRecursive(child));
  }
  return clone;
}

export function quantityForPart(id) {
  const hash = String(id)
    .split("")
    .reduce((a, c) => ((a << 5) - a + c.charCodeAt(0)) | 0, 0);
  const n = ((hash % 100) + 100) % 100;
  if (n < 50) return 1;
  if (n < 78) return 2;
  if (n < 92) return 3;
  return 4;
}

export function PartThumbnailViewer({
  containerRef,
  part,
  modelRef,
  modelLoaded,
  zoomToFill = false,
  enabled = true,
}) {
  const { isDark } = useTheme();
  const THUMB_BG = isDark ? "#2E2E2E" : "#E5E7EB";
  const [inView, setInView] = useState(false);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const rafRef = useRef(null);
  const targetRef = useRef(new THREE.Vector3());

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(e => setInView(e.isIntersecting));
      },
      { rootMargin: "50px", threshold: 0.01 },
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [containerRef]);

  useEffect(() => {
    if (
      !enabled ||
      !inView ||
      !containerRef.current ||
      !part ||
      !modelRef.current ||
      !modelLoaded
    ) {
      return;
    }

    const container = containerRef.current;

    if (!tryAcquireWebGLSlot()) {
      const placeholder = document.createElement("div");
      placeholder.setAttribute("data-webgl-placeholder", "1");
      placeholder.style.cssText = `width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:${isDark ? "#2E2E2E" : "#E5E7EB"};color:${isDark ? "#8C8C8C" : "#6B7280"}`;
      placeholder.innerHTML = `<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>`;
      container.appendChild(placeholder);
      return () => {
        if (placeholder.parentNode === container)
          container.removeChild(placeholder);
      };
    }

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(THUMB_BG);

    // Same lighting as partsViewerShared
    const ambient = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambient);
    const key = new THREE.DirectionalLight(0xffeedd, 3.2);
    key.position.set(0, 0.5, -1.5).normalize();
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xffffff, 0.7);
    fill.position.set(-1.5, 1, 0.5).normalize();
    scene.add(fill);
    const rim = new THREE.DirectionalLight(0xccddff, 3.0);
    rim.position.set(2, 2.5, 1.5).normalize();
    scene.add(rim);
    const top = new THREE.DirectionalLight(0xffffff, 0.6);
    top.position.set(0, 1, 0).normalize();
    scene.add(top);

    const camera = new THREE.PerspectiveCamera(50, 1, 0.001, 1e7);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    const margin = zoomToFill ? 1.02 : 1.15;
    const applyFraming = (radius, center) => {
      const w = Math.max(1, container.clientWidth);
      const h = Math.max(1, container.clientHeight);
      const aspect = w / h;
      const fovVRad = (camera.fov / 2) * (Math.PI / 180);
      const tanHalfFovV = Math.tan(fovVRad);
      const tanHalfFovH = tanHalfFovV * aspect;
      const tanLimit = Math.min(tanHalfFovV, tanHalfFovH);
      const d = (radius / tanLimit) * margin;
      const dir = new THREE.Vector3(1, 1, 1).normalize().multiplyScalar(d);
      camera.position.copy(center).add(dir);
      targetRef.current.copy(center);
    };

    const setSize = () => {
      const w = Math.max(1, container.clientWidth);
      const h = Math.max(1, container.clientHeight);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
      if (framingRef.current) {
        const { radius, center } = framingRef.current;
        applyFraming(radius, center);
      }
    };
    const framingRef = { current: null };
    setSize();
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    container.appendChild(renderer.domElement);
    requestAnimationFrame(setSize);

    const node = findNodeByFullPath(modelRef.current, part.full_path);
    if (node) {
      const clone = cloneNodeRecursive(node);
      scene.add(clone);
      const box = new THREE.Box3().setFromObject(clone);
      const sphere = new THREE.Sphere();
      box.getBoundingSphere(sphere);
      const radius = Math.max(sphere.radius, 1e-6);
      const center = sphere.center.clone();
      framingRef.current = { radius, center };
      // Same as partsViewerShared: distance so entire bounding sphere fits in frame (perspective-aware).
      applyFraming(radius, center);
    }

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableZoom = false;
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.target.copy(targetRef.current);
    controlsRef.current = controls;

    const resizeObserver = new ResizeObserver(setSize);
    resizeObserver.observe(container);

    const animate = () => {
      rafRef.current = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      resizeObserver.disconnect();
      controls.dispose();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (container && renderer.domElement?.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
      renderer.dispose();
      renderer.forceContextLoss();
      releaseWebGLSlot();
    };
  }, [
    enabled,
    inView,
    part,
    modelRef,
    modelLoaded,
    containerRef,
    zoomToFill,
    isDark,
    THUMB_BG,
  ]);

  return null;
}

export function ModelLoader({ modelRef, onLoaded, onReset, modelUrl }) {
  const onLoadedRef = useRef(onLoaded);
  const onResetRef = useRef(onReset);
  onLoadedRef.current = onLoaded;
  onResetRef.current = onReset;
  useEffect(() => {
    onResetRef.current?.();
    let stale = false;
    const loader = new GLTFLoader();
    loader.setDRACOLoader(dracoLoader);
    loader.load(
      modelUrl,
      gltf => {
        if (stale) return;
        modelRef.current = gltf.scene;
        onLoadedRef.current?.();
      },
      undefined,
      err => {
        if (stale) return;
        console.error("ModelLoader: failed to load", modelUrl, err);
        modelRef.current = null;
        onLoadedRef.current?.();
      },
    );
    return () => {
      stale = true;
    };
  }, [modelRef, modelUrl]);
  return null;
}

export function PartCard({
  item,
  partFor3D,
  modelRef,
  modelLoaded,
  zoomToFill = false,
  onDelete,
  onQuantityChange,
  onNameChange,
  showErrors = false,
}) {
  const { isDark } = useTheme();
  const thumbRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isNameFocused, setIsNameFocused] = useState(false);
  const [localName, setLocalName] = useState(item?.name ?? "");
  const [localQuantity, setLocalQuantity] = useState(
    item?.quantity != null && item?.quantity !== ""
      ? String(item?.quantity)
      : "",
  );

  useEffect(() => {
    setLocalName(item?.name ?? "");
  }, [item?.name]);
  useEffect(() => {
    setLocalQuantity(
      item?.quantity != null && item?.quantity !== ""
        ? String(item?.quantity)
        : "",
    );
  }, [item?.quantity]);

  const canEdit = onNameChange != null || onQuantityChange != null;
  const show3D = partFor3D?.full_path && modelRef?.current && modelLoaded;
  const name = canEdit ? localName : (item?.name ?? "");
  const quantity = canEdit ? localQuantity : (item?.quantity ?? "");

  return (
    <Box
      onMouseEnter={() => canEdit && setIsHovered(true)}
      onMouseLeave={() => canEdit && setIsHovered(false)}
      sx={{
        borderRadius: "12px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        border: "1px solid",
        borderColor: isDark ? "#31363B" : "#D5D9DF",
        bgcolor: isDark ? "#262A2E" : "#FFFFFF",
        minWidth: 0,
      }}
    >
      {/* Image / thumbnail area */}
      <Box
        ref={thumbRef}
        onMouseDown={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
        sx={{
          position: "relative",
          aspectRatio: "280/189",
          flexShrink: 0,
          minHeight: 120,
          bgcolor: isDark ? "rgba(25,26,28,0.4)" : "#ECEFF3",
          "& canvas": { display: "block" },
        }}
      >
        {show3D ? (
          <PartThumbnailViewer
            containerRef={thumbRef}
            part={partFor3D}
            modelRef={modelRef}
            modelLoaded={modelLoaded}
            zoomToFill={zoomToFill}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            <BoxIcon
              sx={{ fontSize: 48, color: isDark ? "#8C8C8C" : "#A8AEB6" }}
            />
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 400,
                fontSize: "12px",
                lineHeight: "16px",
                color: isDark ? "#A4A4A4" : "#666666",
              }}
            >
              3D Model Viewer
            </Typography>
          </Box>
        )}

        {/* Delete button — only when caller passes onDelete */}
        {onDelete && (
          <Box
            component='button'
            type='button'
            aria-label='Delete part'
            onClick={e => {
              e.stopPropagation();
              onDelete();
            }}
            onMouseDown={e => e.stopPropagation()}
            sx={{
              position: "absolute",
              top: 10,
              left: 10,
              zIndex: 2,
              width: 32,
              height: 32,
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 0,
              m: 0,
              font: "inherit",
              cursor: "pointer",
              color: isDark ? "#ECEFF3" : "#434950",
              border: "1px solid",
              borderColor: isDark ? "rgba(106,112,121,0.5)" : "#D5D9DF",
              background: isDark
                ? "linear-gradient(162.6deg, rgba(67,73,80,0.8) 0%, rgba(49,54,59,0.8) 50%, rgba(49,54,59,0.8) 100%)"
                : "linear-gradient(162.3deg, rgba(248,249,250,0.8) 0%, rgba(226,231,235,0.9) 50%, rgb(211,218,225) 100%)",
              "&:hover": {
                background: isDark
                  ? "linear-gradient(162.6deg, rgba(77,83,90,0.85) 0%, rgba(59,64,69,0.85) 50%, rgba(59,64,69,0.85) 100%)"
                  : "linear-gradient(162.3deg, rgba(248,249,250,0.95) 0%, rgba(226,231,235,1) 50%, rgb(211,218,225) 100%)",
              },
            }}
          >
            <Trash2 sx={{ width: 16, height: 16 }} />
          </Box>
        )}

        {/* Quantity badge — top right */}
        <Box
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 1,
            width: 32,
            height: 32,
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: isDark ? "#343537" : "#FFFFFF",
            border: "1px solid",
            borderColor:
              showErrors && (localQuantity === "" || localQuantity == null)
                ? "#C12E30"
                : "transparent",
          }}
        >
          {onQuantityChange != null ? (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontSize: "12px",
                  color: isDark ? "#E5E1E1" : "#1A1A1A",
                }}
              >
                x
              </Typography>
              <InputBase
                value={localQuantity}
                onChange={e => setLocalQuantity(e.target.value)}
                onBlur={() => onQuantityChange(localQuantity)}
                onClick={e => e.stopPropagation()}
                onMouseDown={e => e.stopPropagation()}
                inputProps={{
                  style: {
                    width: "2ch",
                    minWidth: "1.5ch",
                    padding: 0,
                    textAlign: "center",
                    fontFamily: "Inter",
                    fontSize: 12,
                    fontWeight: 400,
                    color: isDark ? "#E5E1E1" : "#1A1A1A",
                  },
                }}
                sx={{ "& input": { "&:focus": { outline: "none" } } }}
              />
            </Box>
          ) : (
            <Typography
              sx={{
                fontFamily: "Inter",
                fontSize: "12px",
                color: isDark ? "#E5E1E1" : "#1A1A1A",
              }}
            >
              x {quantity}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Footer — name always stays here */}
      <Box
        sx={{
          minHeight: "41px",
          px: "8px",
          display: "flex",
          alignItems: "center",
        }}
      >
        {isNameFocused && onNameChange ? (
          <Box
            sx={{
              flex: 1,
              height: 30,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              px: "8px",
              bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#FFFFFF",
              border: "1px solid",
              borderColor: isDark ? "#555555" : "#D5D9DF",
            }}
          >
            <Box
              component='input'
              autoFocus
              value={localName}
              onChange={e => setLocalName(e.target.value)}
              onBlur={() => {
                setIsNameFocused(false);
                onNameChange(localName);
              }}
              placeholder='Part name...'
              sx={{
                width: "100%",
                border: "none",
                outline: "none",
                background: "transparent",
                fontFamily: "Inter",
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "0.035px",
                color: isDark ? "#ECEFF3" : "#434950",
                "&::placeholder": {
                  color: isDark
                    ? "rgba(236,239,243,0.35)"
                    : "rgba(67,73,80,0.35)",
                },
              }}
            />
          </Box>
        ) : (
          <Box
            onClick={canEdit ? () => setIsNameFocused(true) : undefined}
            sx={{
              flex: 1,
              height: 30,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              px: "8px",
              cursor: canEdit ? "text" : "default",
              bgcolor:
                canEdit && isHovered
                  ? isDark
                    ? "rgba(58,58,59,0.7)"
                    : "#F3F6FA"
                  : "transparent",
              border: "1px solid",
              borderColor: showErrors && !localName ? "#C12E30" : "transparent",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "0.035px",
                color: isDark ? "#ECEFF3" : "#31363B",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                wordBreak: "break-word",
              }}
            >
              {name || <span style={{ opacity: 0.35 }}>—</span>}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
